// src/components/LoadPatternModal.jsx
import React, { useState, useMemo } from "react";
import { Search } from "lucide-react";
import AutocompleteInput from "./AutocompleteInput";

export function LoadPatternModal({
  onClose,
  savedPatterns,
  onLoadPattern,
  onDeletePattern,
}) {
  const [searchTerm, setSearchTerm] = useState("");

  // Filter patterns based on search
  const filteredPatterns = useMemo(() => {
    if (!searchTerm) return savedPatterns;
    return savedPatterns.filter(
      (pattern) =>
        pattern.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (pattern.folder || "Uncategorized")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
    );
  }, [savedPatterns, searchTerm]);

  // Group patterns by folder
  const groupedPatterns = useMemo(() => {
    return filteredPatterns.reduce((groups, pattern) => {
      const folder = pattern.folder || "Uncategorized";
      if (!groups[folder]) groups[folder] = [];
      groups[folder].push(pattern);
      return groups;
    }, {});
  }, [filteredPatterns]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold">Load Pattern</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Search Bar */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search patterns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Pattern List */}
          {Object.keys(groupedPatterns).length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm ? "No patterns match your search" : "No saved patterns found"}
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedPatterns)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([folder, patterns]) => (
                  <div key={folder} className="border rounded-lg">
                    <div className="bg-gray-50 px-4 py-2 font-medium text-sm border-b">
                      {folder}
                    </div>
                    <div className="p-2">
                      {patterns
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((pattern) => (
                          <div
                            key={pattern.id}
                            className="flex items-center justify-between p-3 hover:bg-gray-50 rounded cursor-pointer"
                            onClick={() => {
                              onLoadPattern(pattern.name, folder);
                              onClose();
                            }}
                          >
                            <span className="flex-1">{pattern.name}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeletePattern(pattern.id);
                              }}
                              className="px-3 py-1 text-red-500 text-sm hover:bg-red-100 rounded"
                            >
                              Delete
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default LoadPatternModal;
